import { useRef, useState } from 'react';
import { useApi } from '@/api/axios';
import { pleasService } from '../services/pleasService';
import type { Attachment, Plea } from '../types';

/**
 * Loads the full attachment list (with scan status) for a plea, tracking
 * loading/error state and guarding against stale responses when a different
 * plea is loaded before the previous request resolves.
 */
export function usePleaAttachments(agreementUuid: string | undefined) {
  const api = useApi();

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  const requestId = useRef('');

  const load = (plea: Plea) => {
    const pleaUuid = plea.self.split('/').pop() ?? '';
    requestId.current = pleaUuid;
    setAttachmentsError(null);
    setAttachments([]);

    if (!(plea.attachmentsCount ?? 0)) {
      setAttachmentsLoading(false);
      return;
    }

    setAttachmentsLoading(true);

    if (!agreementUuid) return;

    pleasService
      .getPleaAttachments(api, agreementUuid, pleaUuid)
      .then((result) => {
        if (requestId.current !== pleaUuid) return;
        setAttachments(result);
        setAttachmentsLoading(false);
      })
      .catch(() => {
        if (requestId.current !== pleaUuid) return;
        setAttachmentsError('Não foi possível carregar os anexos');
        setAttachmentsLoading(false);
      });
  };

  const reset = () => {
    requestId.current = '';
    setAttachments([]);
    setAttachmentsLoading(false);
    setAttachmentsError(null);
  };

  return { attachments, attachmentsLoading, attachmentsError, load, reset };
}
